class TPQuiz extends HTMLElement {

  static TRACKING_STORAGE_KEY = 'tp-quiz-tracking';

  static publishQuizEvent(eventName, options = {}) {
    const eventdata = {
      timestamp: new Date().toISOString(),
      name: options.name != null ? options.name : eventName,
      data: {
        data: options.data != null ? options.data : {},
        type: options.type,
        productid: options.productId,
        variantId: options.variantId
      }
    };
    // Strip undefined values from data so payload stays clean
    Object.keys(eventdata.data).forEach(key => {
      if (eventdata.data[key] === undefined) delete eventdata.data[key];
    });

    // Store in localStorage for testing
    try {
      const raw = localStorage.getItem(TPQuiz.TRACKING_STORAGE_KEY);
      const list = raw ? JSON.parse(raw) : [];
      list.push({ eventName, ...eventdata });
      localStorage.setItem(TPQuiz.TRACKING_STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      console.warn('tp-quiz: could not write tracking to localStorage', e);
    }

    if (typeof Shopify === 'undefined' || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') {
      return;
    }
    Shopify.analytics.publish(eventName, eventdata);
  }

  static selectors = {
    step: '.tp-quiz__step',
    rangeContainer: '.tp-quiz__range-container',
    rangeSlider: '.tp-quiz__range-slider',
    rangeThumbMin: '.tp-quiz__range-thumb--min',
    rangeThumbMax: '.tp-quiz__range-thumb--max',
    rangeInputMin: '.tp-quiz__range-input--min',
    rangeInputMax: '.tp-quiz__range-input--max',
    rangeTrackBg: '.tp-quiz__range-track-bg',
    rangeTrackFill: '.tp-quiz__range-track-fill',
    rangeValue: '.tp-quiz__range-value',
    stepQuestion: '.tp-quiz__step-question',
    stepAnswer: '.tp-quiz__step-answer',
    stepNext: '.tp-quiz__step-next',
    stepPrevious: '.tp-quiz__step-previous',
    stepSubmit: '.tp-quiz__step-submit',
    getStarted: '.tp-quiz__get-started',
    contentWrapper: '.tp-quiz__content-wrapper',
    quizTitle: '.tp-quiz__title'
  };

  constructor() {
    super();
    this.steps = [];
    this.currentStepIndex = 0;
    this.answers = {};
    this.storageKey = 'tp-quiz-answers';
    this.collectionHandle = null;
  }

  connectedCallback() {
    this.collectionHandle = this.dataset.collectionHandle || null;
    this.initializeQuiz();
    this.loadSavedAnswers();
    this.attachEventListeners();
  }

  initializeQuiz() {
    this.steps = Array.from(this.querySelectorAll(TPQuiz.selectors.step));

    this.initializeImages();
    this.initializeRangeSliders();

    if (this.steps.length > 0) {
      this.currentStepIndex = 0;
      this.showStep(0);
    }
  }

  initializeImages() {
    const images = this.querySelectorAll('img');
    images.forEach(image => {
      if (image.dataset.src) {
        image.src = image.dataset.src;
      }
    });
  }

  initializeRangeSliders() {
    const rangeContainers = this.querySelectorAll(TPQuiz.selectors.rangeContainer);
    rangeContainers.forEach(container => {
      this.setupCustomSlider(container);
      this.updateRangeDisplay(container);
    });
  }

  setupCustomSlider(container) {
    const slider = container.querySelector(TPQuiz.selectors.rangeSlider);
    const minThumb = container.querySelector(TPQuiz.selectors.rangeThumbMin);
    const maxThumb = container.querySelector(TPQuiz.selectors.rangeThumbMax);
    const minInput = container.querySelector(TPQuiz.selectors.rangeInputMin);
    const maxInput = container.querySelector(TPQuiz.selectors.rangeInputMax);
    
    if (!slider || !minThumb || !maxThumb || !minInput || !maxInput) return;

    let min = parseInt(container.dataset.rangeMin);
    let max = parseInt(container.dataset.rangeMax);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      const fallbackMin = parseInt(minInput.value);
      const fallbackMax = parseInt(maxInput.value);
      if (Number.isFinite(fallbackMin) && Number.isFinite(fallbackMax)) {
        min = fallbackMin;
        max = fallbackMax;
      } else {
        return;
      }
    }
    
    const minGap = 1;
    if (max - min < minGap) return;
    let activeThumb = null;
    let startX = 0;
    let startValue = 0;
    let sliderRect = null;

    const normalizeRangeInputs = () => {
      let currentMin = parseInt(minInput.value);
      let currentMax = parseInt(maxInput.value);

      if (!Number.isFinite(currentMin)) currentMin = min;
      if (!Number.isFinite(currentMax)) currentMax = max;

      currentMin = Math.max(min, Math.min(max - minGap, currentMin));
      currentMax = Math.max(min + minGap, Math.min(max, currentMax));

      if (currentMin >= currentMax) {
        if (currentMin + minGap <= max) {
          currentMax = currentMin + minGap;
        } else {
          currentMin = currentMax - minGap;
        }
      }

      minInput.value = currentMin;
      maxInput.value = currentMax;
    };

    normalizeRangeInputs();

    const getValueFromPosition = (x) => {
      const rect = slider.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (x - rect.left) / rect.width));
      const value = Math.round(min + percent * (max - min));
      return Math.max(min, Math.min(max, value));
    };

    const handleStart = (e, thumb) => {
      activeThumb = thumb;
      startX = e.touches ? e.touches[0].clientX : e.clientX;
      startValue = parseInt(thumb === minThumb ? minInput.value : maxInput.value);
      sliderRect = slider.getBoundingClientRect();
      thumb.style.transition = 'none';
      thumb.style.zIndex = '3';
      if (thumb === minThumb) {
        maxThumb.style.zIndex = '2';
      } else {
        minThumb.style.zIndex = '2';
      }
      e.preventDefault();
    };

    const handleMove = (e) => {
      if (!activeThumb) return;
      
      const currentX = e.touches ? e.touches[0].clientX : e.clientX;
      const deltaX = currentX - startX;
      const rect = sliderRect || slider.getBoundingClientRect();
      const deltaPercent = deltaX / rect.width;
      const deltaValue = Math.round(deltaPercent * (max - min));
      let newValue = startValue + deltaValue;
      newValue = Math.max(min, Math.min(max, newValue));

      if (activeThumb === minThumb) {
        const maxValue = parseInt(maxInput.value);
        newValue = Math.min(newValue, maxValue - minGap);
        minInput.value = newValue;
      } else {
        const minValue = parseInt(minInput.value);
        newValue = Math.max(newValue, minValue + minGap);
        maxInput.value = newValue;
      }

      this.updateRangeDisplay(container);
    };

    const handleEnd = () => {
      if (activeThumb) {
        activeThumb.style.transition = '';
        activeThumb.style.zIndex = '2';
        activeThumb = null;
        sliderRect = null;
        this.saveRangeAnswer(container);
      }
    };

    minThumb.addEventListener('mousedown', (e) => handleStart(e, minThumb));
    maxThumb.addEventListener('mousedown', (e) => handleStart(e, maxThumb));
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    minThumb.addEventListener('touchstart', (e) => handleStart(e, minThumb), { passive: false });
    maxThumb.addEventListener('touchstart', (e) => handleStart(e, maxThumb), { passive: false });
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    slider.addEventListener('click', (e) => {
      if (e.target === slider || e.target.classList.contains(TPQuiz.selectors.rangeTrackBg.slice(1)) || e.target.classList.contains(TPQuiz.selectors.rangeTrackFill.slice(1))) {
        const newValue = getValueFromPosition(e.clientX);
        const minValue = parseInt(minInput.value);
        const maxValue = parseInt(maxInput.value);
        
        const distToMin = Math.abs(newValue - minValue);
        const distToMax = Math.abs(newValue - maxValue);
        
        if (distToMin < distToMax) {
          const finalValue = Math.min(newValue, maxValue - minGap);
          minInput.value = finalValue;
        } else {
          const finalValue = Math.max(newValue, minValue + minGap);
          maxInput.value = finalValue;
        }
        
        this.updateRangeDisplay(container);
        this.saveRangeAnswer(container);
      }
    });

    const handleKeyDown = (e, thumb, isMin) => {
      let newValue = parseInt(isMin ? minInput.value : maxInput.value);
      const step = 1;
      
      switch(e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault();
          newValue = Math.max(min, newValue - step);
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault();
          newValue = Math.min(max, newValue + step);
          break;
        case 'Home':
          e.preventDefault();
          newValue = min;
          break;
        case 'End':
          e.preventDefault();
          newValue = max;
          break;
        default:
          return;
      }

      if (isMin) {
        newValue = Math.min(newValue, parseInt(maxInput.value) - minGap);
        minInput.value = newValue;
      } else {
        newValue = Math.max(newValue, parseInt(minInput.value) + minGap);
        maxInput.value = newValue;
      }

      this.updateRangeDisplay(container);
      this.saveRangeAnswer(container);
    };

    minThumb.addEventListener('keydown', (e) => handleKeyDown(e, minThumb, true));
    maxThumb.addEventListener('keydown', (e) => handleKeyDown(e, maxThumb, false));
  }

  loadSavedAnswers() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        this.answers = JSON.parse(saved);
        this.restoreAnswers();
      }
    } catch (error) {
      console.error('Error loading saved answers:', error);
      this.answers = {};
    }
  }

  restoreAnswers() {
    let didMigrateKeys = false;

    const stepCache = this.steps.map(step => {
      const rangeContainer = step.querySelector(TPQuiz.selectors.rangeContainer);
      const minInput = rangeContainer ? rangeContainer.querySelector(TPQuiz.selectors.rangeInputMin) : null;
      const maxInput = rangeContainer ? rangeContainer.querySelector(TPQuiz.selectors.rangeInputMax) : null;

      return {
        step,
        questionTitle: this.getQuestionTitle(step),
        questionKey: this.getQuestionKey(step),
        rangeContainer,
        minInput,
        maxInput
      };
    });

    stepCache.forEach(({ step, questionTitle, questionKey, rangeContainer, minInput, maxInput }) => {
      if (!questionKey && !questionTitle) return;

      let savedData = this.answers[questionKey];
      let usedFallbackTitle = false;

      if (!savedData && questionTitle) {
        savedData = this.answers[questionTitle];
        usedFallbackTitle = Boolean(savedData);
      }

      if (savedData) {
        if (usedFallbackTitle && questionKey && questionTitle && questionKey !== questionTitle) {
          this.answers[questionKey] = savedData;
          delete this.answers[questionTitle];
          didMigrateKeys = true;
        }
        
        let savedAnswer;
        if (typeof savedData === 'object' && savedData.answer !== undefined) {
          savedAnswer = savedData.answer;
        }
        
        if (typeof savedAnswer === 'object' && savedAnswer.min !== undefined && savedAnswer.max !== undefined) {
          if (rangeContainer && minInput && maxInput) {
            minInput.value = savedAnswer.min;
            maxInput.value = savedAnswer.max;
            this.updateRangeDisplay(rangeContainer);
          }
        } else if (Array.isArray(savedAnswer)) {
          savedAnswer.forEach(value => {
            const checkbox = step.querySelector(`input[type="checkbox"][value="${this.escapeHtml(value)}"]`);
            if (checkbox) {
              checkbox.checked = true;
            }
          });
        } else {
          const radioInput = step.querySelector(`input[type="radio"][value="${this.escapeHtml(savedAnswer)}"]`);
          if (radioInput) {
            radioInput.checked = true;
          }
        }
      }
    });

    if (didMigrateKeys) {
      this.saveAnswers();
    }
  }

  getQuestionTitle(step) {
    const questionElement = step.querySelector(TPQuiz.selectors.stepQuestion);
    return questionElement ? questionElement.textContent.trim() : '';
  }

  getQuestionId(step) {
    const id = step.dataset.questionId;
    return id ? id.trim() : '';
  }

  getQuestionKey(step) {
    return this.getQuestionId(step) || this.getQuestionTitle(step);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  attachEventListeners() {
    // Answer selection
    this.addEventListener('change', (e) => {
      if (e.target.classList.contains(TPQuiz.selectors.stepAnswer.replace('.', ''))) {
        this.handleAnswerChange(e.target);
      }
    });

    // Next
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains(TPQuiz.selectors.stepNext.slice(1))) {
        e.preventDefault();
        this.goToNextStep();
      }
    });

    // Previous
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains(TPQuiz.selectors.stepPrevious.replace('.', ''))) {
        e.preventDefault();
        this.goToPreviousStep();
      }
    });

    // Finish
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains(TPQuiz.selectors.stepSubmit.slice(1))) {
        e.preventDefault();
        this.handleFinish();
      }
    });

    // Get started
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains(TPQuiz.selectors.getStarted.slice(1)) || 
          e.target.closest(TPQuiz.selectors.getStarted)) {
        e.preventDefault();
        this.goToFirstStep();
      }
    });

    this.addEventListener('toggle', (e) => {
      if (this.matches(':popover-open')) {
        this.loadSavedAnswers();
        this.sendQuizOpenedEvent();
      }
    });
  }

  handleAnswerChange(input) {
    const step = input.closest(TPQuiz.selectors.step);
    const questionKey = this.getQuestionKey(step);
    const answerType = step.dataset.answerType;
    if (!questionKey) return;
    
    if (input.type === 'checkbox') {
      const checkboxes = step.querySelectorAll(`input[name="${input.name}"]${TPQuiz.selectors.stepAnswer}`);
      const selectedValues = Array.from(checkboxes)
        .filter(cb => cb.checked)
        .map(cb => cb.value);
      
      this.saveAnswerWithType(questionKey, selectedValues, answerType);
    } else {
      const answerValue = input.value;
      this.saveAnswerWithType(questionKey, answerValue, answerType);
    }
  }

  saveAnswerWithType(questionTitle, answer, answerType) {
    this.answers[questionTitle] = {
      answer: answer,
      answerType: answerType || null
    };
    this.saveAnswers();

    // Question Answered tracking
    this.publishQuizEvent('quiz_question_answered', {
      name: 'quiz_interaction',
      type: 'question_answered',
      data: {
        questionName: questionTitle,
        answerGiven: answer
      }
    });
  }

  saveRangeAnswer(rangeContainer) {
    const step = rangeContainer.closest(TPQuiz.selectors.step);
    const questionKey = this.getQuestionKey(step);
    const answerType = step.dataset.answerType;
    const minInput = rangeContainer.querySelector(TPQuiz.selectors.rangeInputMin);
    const maxInput = rangeContainer.querySelector(TPQuiz.selectors.rangeInputMax);
    
    if (questionKey && minInput && maxInput) {
      const rangeAnswer = {
        min: parseInt(minInput.value),
        max: parseInt(maxInput.value)
      };
      this.saveAnswerWithType(questionKey, rangeAnswer, answerType);
    }
  }

  updateRangeDisplay(rangeContainer) {
    const minInput = rangeContainer.querySelector(TPQuiz.selectors.rangeInputMin);
    const maxInput = rangeContainer.querySelector(TPQuiz.selectors.rangeInputMax);
    const minThumb = rangeContainer.querySelector(TPQuiz.selectors.rangeThumbMin);
    const maxThumb = rangeContainer.querySelector(TPQuiz.selectors.rangeThumbMax);
    const minValueDisplay = minThumb?.querySelector(TPQuiz.selectors.rangeValue);
    const maxValueDisplay = maxThumb?.querySelector(TPQuiz.selectors.rangeValue);
    const trackFill = rangeContainer.querySelector(TPQuiz.selectors.rangeTrackFill);
    const slider = rangeContainer.querySelector(TPQuiz.selectors.rangeSlider);

    if (!minInput || !maxInput || !slider) return;

    let minValue = parseInt(minInput.value);
    let maxValue = parseInt(maxInput.value);
    let min = parseInt(rangeContainer.dataset.rangeMin);
    let max = parseInt(rangeContainer.dataset.rangeMax);
    const minGap = 1;
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      const fallbackMin = parseInt(minInput.value);
      const fallbackMax = parseInt(maxInput.value);
      if (Number.isFinite(fallbackMin) && Number.isFinite(fallbackMax)) {
        min = fallbackMin;
        max = fallbackMax;
      } else {
        return;
      }
    }
    if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return;

    minValue = Math.max(min, Math.min(max - minGap, minValue));
    maxValue = Math.max(min + minGap, Math.min(max, maxValue));

    if (minValue >= maxValue) {
      if (minValue + minGap <= max) {
        maxValue = minValue + minGap;
      } else {
        minValue = maxValue - minGap;
      }
    }

    minInput.value = minValue;
    maxInput.value = maxValue;

    const range = max - min;
    if (range <= 0) return;

    // Update displayed values
    if (minValueDisplay) {
      minValueDisplay.textContent = minValue;
      minThumb.setAttribute('aria-valuenow', minValue);
    }
    if (maxValueDisplay) {
      maxValueDisplay.textContent = maxValue;
      maxThumb.setAttribute('aria-valuenow', maxValue);
    }

    // Calculate positions
    const sliderWidth = slider.offsetWidth;
    const minPercent = ((minValue - min) / range) * 100;
    const maxPercent = ((maxValue - min) / range) * 100;

    // Position thumbs
    if (minThumb) {
      minThumb.style.left = `${minPercent}%`;
    }
    if (maxThumb) {
      maxThumb.style.left = `${maxPercent}%`;
    }

    // Update track fill
    if (trackFill) {
      trackFill.style.left = `${minPercent}%`;
      trackFill.style.width = `${maxPercent - minPercent}%`;
    }
  }

  saveAnswers() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.answers));
    } catch (error) {
      console.error('Error saving answers:', error);
    }
  }

  goToNextStep() {
    const currentStep = this.steps[this.currentStepIndex];
    const questionKey = this.getQuestionKey(currentStep);
    const answerType = currentStep.dataset.answerType;
    
    // Save current answer if not already saved
    if (questionKey && !this.answers[questionKey]) {
      // Check for range answer
      const rangeContainer = currentStep.querySelector(TPQuiz.selectors.rangeContainer);
      if (rangeContainer) {
        const minInput = rangeContainer.querySelector(TPQuiz.selectors.rangeInputMin);
        const maxInput = rangeContainer.querySelector(TPQuiz.selectors.rangeInputMax);
        if (minInput && maxInput) {
          const rangeAnswer = {
            min: parseInt(minInput.value),
            max: parseInt(maxInput.value)
          };
          this.saveAnswerWithType(questionKey, rangeAnswer, answerType);
        }
      } else {
        // Check for checkbox (multi-select)
        const checkboxes = currentStep.querySelectorAll(`input[type="checkbox"]${TPQuiz.selectors.stepAnswer}`);
        if (checkboxes.length > 0) {
          const selectedValues = Array.from(checkboxes)
            .filter(cb => cb.checked)
            .map(cb => cb.value);
          this.saveAnswerWithType(questionKey, selectedValues, answerType);
        } else {
          // Radio button (single select)
          const selectedAnswer = currentStep.querySelector(`input[type="radio"]${TPQuiz.selectors.stepAnswer}:checked`);
          if (selectedAnswer) {
            this.saveAnswerWithType(questionKey, selectedAnswer.value, answerType);
          }
        }
      }
    }

    // Move to next step
    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex++;
      this.showStep(this.currentStepIndex);
    }
  }

  goToPreviousStep() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.showStep(this.currentStepIndex);
    }
  }

  goToFirstStep() {
    // Quiz Started tracking: title + whether they had existing answers (retaking)
    const quizTitleEl = this.querySelector(TPQuiz.selectors.quizTitle);
    const quizTitle = quizTitleEl ? quizTitleEl.textContent.trim() : '';
    const hadStoredAnswers = (() => {
      try {
        const raw = localStorage.getItem(this.storageKey);
        return !!(raw && raw.length > 0 && JSON.parse(raw) && Object.keys(JSON.parse(raw)).length > 0);
      } catch {
        return false;
      }
    })();
    this.publishQuizEvent('quiz_started', {
      name: 'quiz_interaction',
      type: 'quiz_started',
      data: {
        quizTitle: quizTitle || undefined,
        isRetaking: hadStoredAnswers
      }
    });

    // Navigate to the first actual quiz step (index 1, since index 0 is the intro step)
    if (this.steps.length > 1) {
      this.currentStepIndex = 1;
      this.showStep(this.currentStepIndex);
    }
  }

  showStep(index) {
    // Hide all steps
    this.steps.forEach((step, i) => {
      step.setAttribute('data-active-step', i === index ? 'true' : 'false');
    });

    const contentWrapper = this.querySelector(TPQuiz.selectors.contentWrapper);
    if (contentWrapper && contentWrapper.scrollTop !== 0) {
      contentWrapper.scrollTop = 0;
    }

    // Reinitialize range slider for active step
    const activeStep = this.steps[index];
    if (activeStep) {
      const rangeContainer = activeStep.querySelector(TPQuiz.selectors.rangeContainer);
      if (rangeContainer) {
        requestAnimationFrame(() => {
          this.updateRangeDisplay(rangeContainer);
        });
      }
    }
  }

  handleFinish() {
    // Answers are already saved on each interaction (radio/checkbox changes, range slider moves)
    // This is just a safety net in case the final step's answer wasn't saved
    const currentStep = this.steps[this.currentStepIndex];
    const questionKey = this.getQuestionKey(currentStep);
    const answerType = currentStep.dataset.answerType;
    
    if (questionKey && !this.answers[questionKey]) {
      // Check for range answer
      const rangeContainer = currentStep.querySelector(TPQuiz.selectors.rangeContainer);
      if (rangeContainer) {
        const minInput = rangeContainer.querySelector(TPQuiz.selectors.rangeInputMin);
        const maxInput = rangeContainer.querySelector(TPQuiz.selectors.rangeInputMax);
        if (minInput && maxInput) {
          const rangeAnswer = {
            min: parseInt(minInput.value),
            max: parseInt(maxInput.value)
          };
          this.saveAnswerWithType(questionKey, rangeAnswer, answerType);
        }
      } else {
        // Check for checkbox (multi-select)
        const checkboxes = currentStep.querySelectorAll(`input[type="checkbox"]${TPQuiz.selectors.stepAnswer}`);
        if (checkboxes.length > 0) {
          const selectedValues = Array.from(checkboxes)
            .filter(cb => cb.checked)
            .map(cb => cb.value);
          this.saveAnswerWithType(questionKey, selectedValues, answerType);
        } else {
          // Radio button (single select)
          const selectedAnswer = currentStep.querySelector(`input[type="radio"]${TPQuiz.selectors.stepAnswer}:checked`);
          if (selectedAnswer) {
            this.saveAnswerWithType(questionKey, selectedAnswer.value, answerType);
          }
        }
      }
    }

    console.log('Quiz completed with answers:', this.answers);

    this.redirectToResults();
    this.hidePopover();
  }

  redirectToResults() {
    const filterParams = this.buildFilterParams();
    
    const collectionHandle = this.getCollectionHandle();

    const baseUrl = `/collections/${collectionHandle}`;
    
    const allParams = ['sort_by=price-descending', 'filter.v.availability=1', ...filterParams];
    const url = `${baseUrl}?${allParams.join('&')}`;

    // Quiz submitted tracking: title + full answers object
    const quizTitleEl = this.querySelector(TPQuiz.selectors.quizTitle);
    const quizTitle = quizTitleEl ? quizTitleEl.textContent.trim() : '';
    
    this.publishQuizEvent('quiz_submitted', {
      name: 'quiz_interaction',
      type: 'quiz_submitted',
      data: {
        quizTitle: quizTitle || undefined,
        answers: this.getAnswers(),
        url: url,
        collectionHandle: collectionHandle
      }
    });

    localStorage.setItem('tp-quiz-url', url);
    
    window.location.href = url;
  }

  buildFilterParams() {
    const params = [];
    
    Object.entries(this.answers).forEach(([questionTitle, answerData]) => {
      let answerValue, answerType;

      if (typeof answerData === 'object' && answerData.answer !== undefined) {
        answerValue = answerData.answer;
        answerType = answerData.answerType;
      }
      
      // Skip if answerType is none, null, or undefined, skip
      if (!answerType || answerType === 'none' || !answerValue) return;
      
      switch (answerType) {
        case 'price':
          const priceFilters = this.mapBudgetToPrice(answerValue);
          if (priceFilters && priceFilters.length > 0) {
            params.push(...priceFilters);
          } else {
            params.push('filter.v.price.gte=');
            params.push('filter.v.price.lte=');
          }
          break;
          
        case 'tag':
          if (Array.isArray(answerValue) && answerValue.length > 0) {
            const tags = answerValue.map(val => val).join(',');
            params.push(`filter.p.tag=${encodeURIComponent(tags)}`);
          } else {
            params.push(`filter.p.tag=${encodeURIComponent(answerValue)}`);
          }
          break;
          
        case 'age':
          if (typeof answerValue === 'object' && answerValue.min !== undefined) {
      
            const minAge = parseInt(answerValue.min);
            const maxAge = parseInt(answerValue.max);
            
            let ageValues = [];

            for (let age = minAge; age <= maxAge; age++) {
              ageValues.push(age);
            }

            if (ageValues.length > 0) {
              params.push(`filter.p.m.product_data.recommended_user_age=${ageValues.join(',')}`);
            }

          }
          break;
            
        case 'size':
          params.push(`filter.p.m.custom.garden_size=${answerValue}`);
          break;

        case 'children':
          let childrenValues = [];

          for (let children = 6; children >= answerValue; children--) {
            childrenValues.push(children);
          }

          params.push(`filter.p.m.custom.max_number_of_children=${childrenValues.join(',')}`);
          break;

        case 'features':
          if (Array.isArray(answerValue) && answerValue.length > 0) {
            answerValue.forEach(feature => {
              params.push(`filter.p.m.product_data.product_features=${encodeURIComponent(feature)}`);
            });
          }
          break;

        case 'growable':
          switch (answerValue) {
            case "Both":
              params.push(`filter.p.m.custom.growable_or_modular=Added Accessories,Grows with my child`);
              break;
            case "Modular":
              params.push(`filter.p.m.custom.growable_or_modular=Added Accessories`);
              break;
            case "Growable":
              params.push(`filter.p.m.custom.growable_or_modular=Grows with my child`);
              break;
          }
          break;
      }
    });
    
    return params;
  }

  getStepByQuestionTitle(questionTitle) {
    return this.steps.find(step => {
      const stepQuestionTitle = this.getQuestionTitle(step);
      return stepQuestionTitle === questionTitle;
    });
  }

  mapBudgetToPrice(budget) {
    const priceMap = {
      'under-200': ['filter.v.price.lte=200', 'filter.v.price.gte='],
      '200-400': ['filter.v.price.gte=200', 'filter.v.price.lte=400'],
      '400-800': ['filter.v.price.gte=400', 'filter.v.price.lte=800'],
      '800-over': ['filter.v.price.gte=800', 'filter.v.price.lte=']
    };
    
    return priceMap[budget] || null;
  }

  getCollectionHandle() {
    return this.collectionHandle || "all";
  }

  sendQuizOpenedEvent() {
    this.publishQuizEvent('quiz_opened', {
      name: 'quiz_interaction',
      type: 'quiz_opened',
      data: {
        quizTitle: (this.querySelector(TPQuiz.selectors.quizTitle) || {}).textContent?.trim() || undefined
      }
    });
  }

  /**
   * Instance helper to publish a quiz analytics event. Forwards to TPQuiz.publishQuizEvent.
   */
  publishQuizEvent(eventName, options = {}) {
    TPQuiz.publishQuizEvent(eventName, options);
  }

  clearAnswers() {
    localStorage.removeItem(this.storageKey);
    this.answers = {};
    this.restoreAnswers();
  }

  getAnswers() {
    return { ...this.answers };
  }
}

customElements.define('tp-quiz', TPQuiz);
